import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const KnowledgeTransferPortal = ({ departure, onClose }) => {
  const [documents, setDocuments] = useState([
    {
      id: 1,
      title: 'Project Documentation',
      description: 'Complete project files and documentation',
      category: 'Projects',
      status: 'completed',
      transferredTo: 'John Smith',
      transferDate: '2026-01-25'
    },
    {
      id: 2,
      title: 'Client Contact List',
      description: 'Key client contacts and communication history',
      category: 'Contacts',
      status: 'in-progress',
      transferredTo: 'Mary Johnson',
      transferDate: null
    },
    {
      id: 3,
      title: 'Process Documentation',
      description: 'Standard operating procedures and workflows',
      category: 'Processes',
      status: 'pending',
      transferredTo: 'Peter Williams',
      transferDate: null
    },
    {
      id: 4,
      title: 'System Access Credentials',
      description: 'Shared system credentials and access information',
      category: 'Systems',
      status: 'pending',
      transferredTo: 'IT Administrator',
      transferDate: null
    }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'in-progress':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'pending':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleStatusChange = (docId, newStatus) => {
    setDocuments(prev =>
      prev?.map(doc =>
        doc?.id === docId
          ? { ...doc, status: newStatus, transferDate: newStatus === 'completed' ? new Date()?.toISOString()?.split('T')?.[0] : null }
          : doc
      )
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 p-2 rounded-lg">
            <Icon name="BookOpen" size={24} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Knowledge Transfer Portal</h2>
            <p className="text-sm text-gray-600">{departure?.employeeName}</p>
          </div>
        </div>
        <Button onClick={onClose} variant="outline" size="sm">
          <Icon name="X" size={16} className="mr-1" />
          Close
        </Button>
      </div>

      {/* Progress Summary */}
      <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 mb-6 border border-amber-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-amber-700">
            Knowledge Transfer Progress
          </span>
          <span className="text-sm font-bold text-amber-900">
            {documents?.filter(d => d?.status === 'completed')?.length} / {documents?.length} Completed
          </span>
        </div>
        <div className="w-full bg-amber-200 rounded-full h-2">
          <div
            className="bg-amber-600 h-2 rounded-full transition-all"
            style={{
              width: `${(documents?.filter(d => d?.status === 'completed')?.length / documents?.length) * 100}%`
            }}
          />
        </div>
      </div>

      {/* Document List */}
      <div className="space-y-3">
        {documents?.map(doc => (
          <div key={doc?.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-900">{doc?.title}</h3>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    {doc?.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{doc?.description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    Transfer to: {doc?.transferredTo}
                  </span>
                  {doc?.transferDate && (
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      {new Date(doc?.transferDate)?.toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                    doc?.status
                  )}`}
                >
                  {doc?.status?.replace('-', ' ')?.toUpperCase()}
                </span>
                {doc?.status !== 'completed' && (
                  <Button
                    onClick={() => handleStatusChange(doc?.id, 'completed')}
                    size="sm"
                    className="bg-amber-600 text-white"
                  >
                    <Icon name="Check" size={14} className="mr-1" />
                    Complete
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add New Transfer Item */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <Button variant="outline" className="w-full">
          <Icon name="Plus" size={16} className="mr-2" />
          Add Transfer Item
        </Button>
      </div>
    </div>
  );
};

export default KnowledgeTransferPortal;